import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";

import HomePage from "./pages/HomePage";
import ContestDrillPage from "./pages/ContestDrillPage";
import RatingLabPage from "./pages/RatingLabPage";
import Navbar from "./components/Navbar";
import Login from "./components/Login";
import WelcomeModal, { shouldShowWelcomeModal } from "./components/WelcomeModal";
import { onAuthStateChanged } from "firebase/auth";
import { getIdToken } from "firebase/auth";
import { auth } from "./firebase";
import { syncLeetCode } from "./services/api";
import { checkExtensionPresent, requestLeetCodeSync } from "./services/extensionBridge";

const AUTO_SYNC_KEY = "roundprep_last_auto_sync";

/** Run a silent background LeetCode sync at most once per calendar day. */
async function runDailyAutoSync(firebaseUser) {
  const today = new Date().toISOString().slice(0, 10); // "2026-07-20"
  if (localStorage.getItem(AUTO_SYNC_KEY) === today) return;
  if (localStorage.getItem("roundprep_lc_synced") !== "true") return;

  // Give the content script a moment to load after navigation.
  await new Promise((r) => setTimeout(r, 2000));

  const present = await checkExtensionPresent();
  if (!present) return;

  const data = await requestLeetCodeSync(20_000).catch(() => null);
  if (!data?.ok || !data?.username) return;

  try {
    const idToken = await getIdToken(firebaseUser, false);
    await syncLeetCode(
      idToken,
      data.username,
      data.questions,
      data.contestRating,
      data.contestsAttended,
    );
    localStorage.setItem(AUTO_SYNC_KEY, today);
    localStorage.setItem("lc_username", data.username);
    if (data.contestsAttended != null)
      localStorage.setItem("lc_contests_attended", data.contestsAttended);
    if (data.contestRating != null)
      localStorage.setItem("lc_contest_rating", Math.round(data.contestRating));
  } catch {
    // Silently ignore — will retry on next visit
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [streakVersion, setStreakVersion] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const userData = {
          uid: firebaseUser.uid,
          name: firebaseUser.displayName,
          email: firebaseUser.email,
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
        if (shouldShowWelcomeModal()) {
          setShowWelcome(true);
        }
        // Silent daily background sync — bumps streakVersion on success.
        runDailyAutoSync(firebaseUser).then(() => {
          const today = new Date().toISOString().slice(0, 10);
          if (localStorage.getItem(AUTO_SYNC_KEY) === today) {
            setStreakVersion((v) => v + 1);
          }
        });
      } else {
        setUser(null);
        localStorage.removeItem("user");
        setShowWelcome(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
        }}
      >
        {/* Spinner ring */}
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: '1.5px solid rgba(255,255,255,0.06)',
            borderTop: '1.5px solid rgba(96,165,250,0.7)',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <span style={{ color: 'rgba(148,163,184,0.5)', fontSize: 13, fontFamily: 'var(--font-display)' }}>
          Loading…
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <Router>
      <div style={{ minHeight: '100vh' }}>

        {/* ── WELCOME MODAL ── */}
        {showWelcome && user && (
          <WelcomeModal user={user} onClose={() => setShowWelcome(false)} />
        )}

        {/* ── NAVBAR (full width) ── */}
        <Navbar user={user} setUser={setUser} onConnectLeetCode={() => setShowWelcome(true)} streakVersion={streakVersion} />

        {/* ── CONTENT ── */}
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 28px' }}>

          {/* ── LOGIN ── */}
          {!user && (
            <div className="flex justify-center" style={{ padding: '16px 0' }}>
              <Login setUser={setUser} />
            </div>
          )}

          {/* ── ROUTES ── */}
          <div style={{ padding: '8px 0' }}>
            <Routes>
              <Route path="/" element={<HomePage user={user} />} />
              <Route path="/contest-drill" element={<ContestDrillPage user={user} />} />
              <Route path="/rating-lab" element={<RatingLabPage user={user} />} />
            </Routes>
          </div>

        </div>
      </div>
    </Router>
  );
}

export default App;
